import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Play, Clock, X } from 'lucide-react';
import { audioManager } from '../utils/audioManager';

/**
 * "A Double Feature" — the cinematic centerpiece of the Jubilee homepage.
 *
 * Two large poster-style film cards (IPMI history + IPMI Foundation history).
 * Click a card → the poster is replaced INLINE with an embedded YouTube iframe
 * (no modal / no body-scroll lock). This is intentional so the section behaves
 * cleanly when the page is embedded inside another site via <iframe>.
 *
 * - Clicking a second card auto-stops the first (only one film plays at a time).
 * - Click the small X to stop and bring back the poster.
 * - Background music auto-pauses while any film is playing.
 */
const films = [
  {
    id: 'ipmi-50',
    eyebrow: 'IPMI · The Institute',
    title: '50 Years of Precious Metals',
    subtitle: 'The IPMI Story',
    poster:
      'https://customer-assets.emergentagent.com/job_ipmi-golden/artifacts/sjrytm2s_IPMI%20History%20YT%20TN.jpg',
    youtubeId: 'KwuQcIVsxag',
    runtime: 'Anniversary Film',
    description:
      'From a 1976 founding at the Princeton Club to a global community of 600+ organizations — the story of the people who built IPMI.',
    cta: 'Play the History of the Institute film',
  },
  {
    id: 'foundation',
    eyebrow: 'IPMI Foundation · 50 Years',
    title: 'Building the Future of Precious Metals',
    subtitle: 'The IPMI Foundation',
    poster:
      'https://customer-assets.emergentagent.com/job_ipmi-golden/artifacts/02w2ubp7_IPMI%20Foundation%20YT.png',
    youtubeId: '--Q9nxZNRKg',
    runtime: 'Foundation Film',
    description:
      'Bridging academia and industry through scholarships, research, and the Tyler, Albert, and Tanaka awards — investing in the people who will define the future.',
    cta: 'Play the History of the Foundation film',
  },
];

const FilmsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });
  const [playingId, setPlayingId] = useState(null);

  // Pause background music while a film is playing; resume when stopped
  useEffect(() => {
    if (playingId) {
      audioManager.pauseBackgroundMusic();
    } else {
      audioManager.resumeBackgroundMusic();
    }
  }, [playingId]);

  const handlePlay = (id) => setPlayingId(id);
  const handleStop = (e) => {
    e.stopPropagation();
    setPlayingId(null);
  };

  return (
    <section className="films-section" ref={sectionRef} id="films">
      {/* Ambient backdrop */}
      <div className="films-ambient" aria-hidden="true">
        <div className="films-ambient-glow films-ambient-glow--gold" />
        <div className="films-ambient-glow films-ambient-glow--navy" />
        <div className="films-ambient-stars" />
      </div>

      {/* Film cards */}
      <div className="films-grid">
        {films.map((film, idx) => {
          const isPlaying = playingId === film.id;
          return (
            <motion.article
              key={film.id}
              className={`film-card${isPlaying ? ' film-card--playing' : ''}`}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.7 + idx * 0.18, ease: [0.22, 1, 0.36, 1] }}
              whileHover={!isPlaying ? { y: -8 } : {}}
            >
              {/* Poster / Player */}
              <div className="film-card-poster">
                {isPlaying ? (
                  <>
                    <iframe
                      className="film-card-iframe"
                      src={`https://www.youtube-nocookie.com/embed/${film.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                      title={film.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                    <button
                      type="button"
                      className="film-card-stop"
                      onClick={handleStop}
                      aria-label={`Stop ${film.title}`}
                      data-testid={`film-stop-${film.id}`}
                    >
                      <X size={18} />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="film-card-poster-btn"
                    onClick={() => handlePlay(film.id)}
                    aria-label={`Play ${film.title}`}
                    data-testid={`film-play-${film.id}`}
                  >
                    <img src={film.poster} alt={`${film.title} — poster`} loading="lazy" />
                    <div className="film-card-poster-shade" />
                    <div className="film-card-play">
                      <span className="film-card-play-ring" />
                      <span className="film-card-play-ring film-card-play-ring--delay" />
                      <span className="film-card-play-btn">
                        <Play size={28} fill="currentColor" />
                      </span>
                    </div>
                    <div className="film-card-runtime">
                      <Clock size={13} />
                      <span>{film.runtime}</span>
                    </div>
                  </button>
                )}
              </div>

              {/* Caption */}
              <div className="film-card-caption">
                <span className="film-card-eyebrow">{film.eyebrow}</span>
                <h3 className="film-card-title">
                  {film.title}
                  <span className="film-card-subtitle">{film.subtitle}</span>
                </h3>
                <p className="film-card-desc">{film.description}</p>
                {!isPlaying && (
                  <button
                    type="button"
                    className="film-card-cta"
                    onClick={() => handlePlay(film.id)}
                    data-testid={`film-cta-${film.id}`}
                  >
                    {film.cta}
                    <svg width="18" height="10" viewBox="0 0 18 10" fill="none" aria-hidden="true">
                      <path d="M1 5h15M12 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
};

export default FilmsSection;
